import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MarkerData, ProcessedMarkerGroup, GeoManagerCaptions } from '../geo-manager.interfaces';
import { DocumentNavigationComponent } from '../../document-navigation/document-navigation.component';

@Component({
  selector: 'app-marker-drawer',
  standalone: true,
  imports: [CommonModule, DocumentNavigationComponent],
  template: `
    <div class="drawer-panel absolute top-0 right-0 h-full w-96 bg-white shadow-xl transform transition-transform duration-300 z-50 flex flex-col"
         [class.translate-x-0]="isOpen"
         [class.translate-x-full]="!isOpen">
      
      <!-- Drawer header -->
      <div class="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
        <h2 class="text-lg font-semibold text-gray-800">{{ captions.markerDetailsTitle }}</h2>
        <button 
          *ngIf="!preventDismiss"
          (click)="onClose()"
          class="p-1 rounded-full hover:bg-gray-100 transition-colors">
          <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <!-- Drawer content -->
      <div class="drawer-content p-4 overflow-y-auto flex-1">
        <!-- Marker info -->
        <div class="space-y-4">
          <!-- Title and subtitle -->
          <div>
            <h3 class="text-xl font-bold text-gray-800 mb-1">{{ marker.title }}</h3>
            <p class="text-lg font-medium text-gray-600 mb-2" *ngIf="marker.subtitle">{{ marker.subtitle }}</p>
            <p class="text-gray-600 mb-4" *ngIf="marker.description">{{ marker.description }}</p>
          </div>

          <!-- Group info -->
          <div class="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
            <div 
              class="w-4 h-4 rounded-full border border-gray-300"
              [style.backgroundColor]="group.bgColor">
            </div>
            <span class="font-medium text-gray-700">{{ group.groupName }}</span>
          </div>

          <!-- Coordinates -->
          <div class="p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
            <h4 class="font-medium text-gray-700 mb-1">{{ captions.locationLabel }}</h4>
            <p class="text-sm text-gray-600">
              {{ captions.latitudeLabel }}: {{ marker.latitude.toFixed(6) }}
            </p>
            <p class="text-sm text-gray-600">
              {{ captions.longitudeLabel }}: {{ marker.longitude.toFixed(6) }}
            </p>
          </div>

          <!-- Custom properties -->
          <div *ngIf="marker.customProperties && customPropertiesArray.length > 0" 
               class="space-y-2">
            <h4 class="font-medium text-gray-700">{{ captions.additionalInfoLabel }}</h4>
            <div class="space-y-2">
              <div 
                *ngFor="let prop of customPropertiesArray; trackBy: trackByKey" 
                class="flex justify-between items-start p-2 bg-gray-50 rounded">
                <span class="font-medium text-gray-600 capitalize">{{ prop.key }}:</span>
                <span class="text-gray-800 text-right ml-2">
                  <!-- Caso: Propiedad es un documento -->
                  <app-document-navigation
                    *ngIf="prop.value?.isDocument"
                    [documentType]="prop.value.type"
                    [documentSerial]="prop.value.serial"
                    [sourceTransactions]="prop.value.sourceTransactions"
                    [displayText]="prop.value.serial || '-'"
                    [showIcon]="prop.value.serial && prop.value.serial.trim() !== '' && prop.value.serial !== 'null'">
                  </app-document-navigation>

                  <span *ngIf="!prop.value?.isDocument && !isArray(prop.value)">
                    {{ prop.value }}
                  </span>

                  <ul *ngIf="isArray(prop.value)" class="text-right">
                    <li *ngFor="let item of prop.value" class="text-sm">{{ item }}</li>
                  </ul>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .drawer-panel {
      box-shadow: -10px 0 25px rgba(0, 0, 0, 0.15);
      z-index: 1001;
    }

    .drawer-content {
      scrollbar-width: thin;
      scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
    }

    .drawer-content::-webkit-scrollbar {
      width: 6px;
    }

    .drawer-content::-webkit-scrollbar-track {
      background: transparent;
    }

    .drawer-content::-webkit-scrollbar-thumb {
      background: rgba(156, 163, 175, 0.5);
      border-radius: 3px;
    }

    .drawer-content::-webkit-scrollbar-thumb:hover {
      background: rgba(156, 163, 175, 0.7);
    }
  `]
})
export class MarkerDrawerComponent implements OnInit, OnDestroy {
  @Input() marker!: MarkerData;
  @Input() group!: ProcessedMarkerGroup;
  @Input() isOpen = false;
  @Input() preventDismiss = false; // Prevent closing the drawer
  @Input() captions: GeoManagerCaptions = {
    markerDetailsTitle: 'Detalles del Marcador',
    locationLabel: 'Ubicación',
    latitudeLabel: 'Latitud',
    longitudeLabel: 'Longitud',
    additionalInfoLabel: 'Información Adicional'
  }; // Default Spanish captions
  @Output() close = new EventEmitter<void>();

  customPropertiesArray: { key: string, value: any }[] = [];

  ngOnInit(): void {
    this.processCustomProperties();
    console.log('MarkerDrawerComponent created for marker:', this.marker?.title);
  }

  ngOnDestroy(): void {
    // Clean up when component is destroyed
    console.log('MarkerDrawerComponent destroyed for marker:', this.marker?.title);
    this.customPropertiesArray = [];
  }

  private processCustomProperties(): void {
    this.customPropertiesArray = this.marker.customProperties 
      ? Object.entries(this.marker.customProperties).map(([key, value]) => ({ key, value }))
      : [];
  }

  onClose(): void {
    console.log('Drawer close button clicked'); // Debug log
    this.close.emit();
    console.log('Close event emitted'); // Debug log
  }

  trackByKey(index: number, item: { key: string, value: any }): string {
    return item.key;
  }

  isArray(value: any): boolean {
    return Array.isArray(value);
  }
}
